
import { useState } from "react";
import { Brain } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AnalysisCategoriesList } from "./analysis/AnalysisCategoriesList";
import { AnalysisSearchBar } from "./analysis/AnalysisSearchBar";
import { useAnalysisData } from "./analysis/useAnalysisData";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertCircle } from "lucide-react";

interface CandidateAnalysisTabProps {
  candidateId: string;
  analysis: any;
}

export const CandidateAnalysisTab = ({ candidateId, analysis }: CandidateAnalysisTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Use the hook to process the analysis data
  const { categories, overallScore } = useAnalysisData(analysis);

  // No analysis data available
  if (!analysis || Object.keys(analysis).length === 0) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-xl flex items-center">
            <Brain className="mr-2 h-5 w-5 text-blue-500" />
            AI Candidate Analysis
          </CardTitle>
          <CardDescription>
            No AI analysis available for this candidate yet.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Group categories by type
  const hardSkills = categories.filter(cat => cat.group === "Hard Skills & Experience");
  const workCulture = categories.filter(cat => cat.group === "Work Environment & Culture Preferences");
  const workload = categories.filter(cat => cat.group === "Workload & Expectations");
  const innovation = categories.filter(cat => cat.group === "Innovation & Adaptability");
  const management = categories.filter(cat => cat.group === "Management & Oversight Preferences");
  const compensation = categories.filter(cat => cat.group === "Compensation & Career Growth");
  const remoteWork = categories.filter(cat => cat.group === "Remote Work & Flexibility");
  
  // Get strengths and weaknesses if available
  const strengths = categories.find(cat => cat.key === "strengths" || cat.key === "key_strengths");
  const weaknesses = categories.find(cat => cat.key === "weaknesses" || cat.key === "development_areas" || cat.key === "potential_concerns");
  const summary = categories.find(cat => cat.key === "summary" || cat.key === "overall" || cat.key === "overall_assessment");

  return (
    <div className="space-y-6">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-xl flex items-center">
            <Brain className="mr-2 h-5 w-5 text-blue-500" />
            AI Candidate Analysis
          </CardTitle>
          <CardDescription>
            Detailed AI analysis of the candidate's profile, skills, and preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Overall score display if available */}
          {overallScore !== null && (
            <div className="bg-blue-50 p-4 rounded-md mb-6">
              <h4 className="font-medium text-blue-800 mb-2">Overall Assessment</h4>
              <div className="flex items-center justify-between">
                <p className="text-sm text-blue-700">{summary ? summary.content : "Overall candidate assessment"}</p>
                <span className="text-2xl font-bold text-blue-600 ml-4">{Math.round(overallScore)}%</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Skills section */}
            {hardSkills.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Hard Skills & Experience</h4>
                
                {hardSkills.map((skill, index) => (
                  <div key={index} className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span>{skill.title}</span>
                      {skill.score !== undefined && (
                        <span>{Math.round(skill.score)}%</span>
                      )}
                    </div>
                    {skill.score !== undefined && (
                      <Progress value={skill.score} className="h-2" />
                    )}
                    {typeof skill.content === 'string' && skill.content.length > 0 && (
                      <p className="text-sm text-gray-600 mt-1">{skill.content}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Strengths & Weaknesses */}
            <div className="space-y-3">
              {strengths && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Key Strengths</h4>
                  {Array.isArray(strengths.content) ? (
                    <ul className="space-y-1">
                      {strengths.content.map((strength, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          {typeof strength === 'string' ? strength : JSON.stringify(strength)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-600">{strengths.content}</p>
                  )}
                </div>
              )}
              
              {weaknesses && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Development Areas</h4>
                  {Array.isArray(weaknesses.content) ? (
                    <ul className="space-y-1">
                      {weaknesses.content.map((weakness, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-start">
                          <AlertCircle className="h-3.5 w-3.5 text-amber-500 mr-2 mt-0.5" />
                          {typeof weakness === 'string' ? weakness : JSON.stringify(weakness)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-600">{weaknesses.content}</p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <Separator />
          
          {/* Metrics section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {/* Work Culture */}
            {workCulture.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Work Culture Preferences</h4>
                
                <div className="space-y-4">
                  {workCulture.map((pref, index) => (
                    <div key={index} className="space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span>{pref.title}</span>
                        {pref.score !== undefined && (
                          <span>{Math.round(pref.score)}%</span>
                        )}
                      </div>
                      {pref.score !== undefined && (
                        <Progress value={pref.score} className="h-2" />
                      )}
                      {typeof pref.content === 'string' && pref.content.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">{pref.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Workload & Management */}
            <div className="space-y-6">
              {workload.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Workload Expectations</h4>
                  
                  <div className="space-y-4">
                    {workload.map((pref, index) => (
                      <div key={index} className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span>{pref.title}</span>
                          {pref.score !== undefined && (
                            <span>{Math.round(pref.score)}%</span>
                          )}
                        </div>
                        {pref.score !== undefined && (
                          <Progress value={pref.score} className="h-2" />
                        )}
                        {typeof pref.content === 'string' && pref.content.length > 0 && (
                          <p className="text-sm text-gray-600 mt-1">{pref.content}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {management.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Management Preferences</h4>
                  
                  <div className="space-y-4">
                    {management.map((pref, index) => (
                      <div key={index} className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span>{pref.title}</span>
                          {pref.score !== undefined && (
                            <span>{Math.round(pref.score)}%</span>
                          )}
                        </div>
                        {pref.score !== undefined && (
                          <Progress value={pref.score} className="h-2" />
                        )}
                        {typeof pref.content === 'string' && pref.content.length > 0 && (
                          <p className="text-sm text-gray-600 mt-1">{pref.content}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <Separator />





          {/* Bottom section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Innovation section */}
            {innovation.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Innovation & Adaptability</h4>
                
                <div className="space-y-4">
                  {innovation.map((pref, index) => (
                    <div key={index} className="space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span>{pref.title}</span>
                        {pref.score !== undefined && (
                          <span>{Math.round(pref.score)}%</span>
                        )}
                      </div>
                      {pref.score !== undefined && (
                        <Progress value={pref.score} className="h-2" />
                      )}
                      {typeof pref.content === 'string' && pref.content.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">{pref.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Remote Work & Compensation */}
            <div className="space-y-6">
              {remoteWork.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Remote Work Flexibility</h4>
                  
                  <div className="space-y-4">
                    {remoteWork.map((pref, index) => (
                      <div key={index} className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span>{pref.title}</span>
                          {pref.score !== undefined && (
                            <span>{Math.round(pref.score)}%</span>
                          )}
                        </div>
                        {pref.score !== undefined && (
                          <Progress value={pref.score} className="h-2" />
                        )}
                        {typeof pref.content === 'string' && pref.content.length > 0 && (
                          <p className="text-sm text-gray-600 mt-1">{pref.content}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {compensation.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Compensation & Career Preferences</h4>
                  
                  <div className="space-y-4">
                    {compensation.map((pref, index) => (
                      <div key={index} className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span>{pref.title}</span>
                          {pref.score !== undefined && (
                            <span>{Math.round(pref.score)}%</span>
                          )}
                        </div>
                        {pref.score !== undefined && (
                          <Progress value={pref.score} className="h-2" />
                        )}
                        {typeof pref.content === 'string' && pref.content.length > 0 && (
                          <p className="text-sm text-gray-600 mt-1">{pref.content}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Advanced search section */}
          <Separator />
          
          <div className="mt-6">
            <h4 className="font-medium text-gray-900 mb-3">Advanced Search</h4>
            
            <AnalysisSearchBar 
              searchQuery={searchQuery} 
              setSearchQuery={setSearchQuery} 
            />
            
            {searchQuery && (
              <div className="h-[400px] border rounded-md overflow-hidden bg-gray-50 mt-4">
                <AnalysisCategoriesList 
                  categories={categories} 
                  searchQuery={searchQuery}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};